import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type {
  APIRemark,
  APIRemarkFile,
  APIApplicationPayment,
  APIBinderPDF,
  APISigning,
  APIPersonalHomeApplicationForm,
  APIRemarkStatus,
  APIAppeal,
  APIBaraActiveReferral,
  APIsupplementFile,
} from 'types/api.types';

import type { HomePersonalFormState } from './homePersonalFormReducer.types';

const initialState: HomePersonalFormState = {
  isDataSet: false,
  isDataSaved: false,
  uuid: null,
  data: null,
  remarks: [],
  files: [],
  missingMandatoryFields: [],
  payments: null,
  binder_pdf: null,
  signing: null,
  appeal: null,
  bara_referral: null,
  wip_status: null,
  supplements: [],
};

export const homePersonalFormSlice = createSlice({
  name: 'homePersonalForm',
  initialState,
  reducers: {
    storeData: (
      state,
      action: PayloadAction<{
        uuid: string;
        data: APIPersonalHomeApplicationForm;
        remarks: APIRemark[];
        files: APIRemarkFile[];
        payments: APIApplicationPayment[];
        binder_pdf: APIBinderPDF | null;
        signing: APISigning | null;
        appeal: APIAppeal | null;
        bara_referral: APIBaraActiveReferral | null;
        wip_status: number | null;
        supplements: APIsupplementFile[];
      }>,
    ) => {
      state.uuid = action.payload.uuid;
      state.data = {
        ...action.payload.data,
        additional_insureds: action.payload.data.additional_insureds ?? [],
      };

      state.remarks = action.payload.remarks;
      state.files = action.payload.files;
      state.payments = action.payload.payments;
      state.binder_pdf = action.payload.binder_pdf;
      state.signing = action.payload.signing;
      state.appeal = action.payload.appeal;
      state.bara_referral = action.payload.bara_referral;
      state.wip_status = action.payload.wip_status;
      state.supplements = action.payload.supplements;

      state.missingMandatoryFields = [];
      state.isDataSet = true;
    },

    resetData: (state) => {
      state.isDataSet = false;
      state.uuid = null;
      state.data = null;
      state.remarks = [];
      state.files = [];
      state.supplements = [];
    },

    addMissingMandatoryField: (
      state,
      action: PayloadAction<{
        name: string;
        section: number;
      }>,
    ) => {
      const fieldExists = state.missingMandatoryFields.some(
        (field) => field.name === action.payload.name,
      );

      if (!fieldExists) {
        state.missingMandatoryFields.push({
          name: action.payload.name,
          section: action.payload.section,
        });
        state.isDataSaved = false;
      }
    },

    removeMissingMandatoryField: (state, action: PayloadAction<string>) => {
      const fieldExists = state.missingMandatoryFields.some(
        (field) => field.name === action.payload,
      );

      if (fieldExists) {
        state.missingMandatoryFields = state.missingMandatoryFields.filter(
          (field) => field.name !== action.payload,
        );
        state.isDataSaved = false;
      }
    },

    addRemark: (state, action: PayloadAction<APIRemark>) => {
      if (
        !state.remarks.some(
          (remark: any) =>
            remark.validation_key === action.payload.validation_key &&
            remark.item_key === action.payload.item_key,
        )
      ) {
        state.remarks.push(action.payload);
      }
    },

    removeRemark: (
      state,
      action: PayloadAction<{ validation_key: string; item_key: string }>,
    ) => {
      state.remarks = state.remarks.filter(
        (remark: any) =>
          !(
            remark.validation_key === action.payload.validation_key &&
            remark.item_key === action.payload.item_key
          ),
      );
    },

    updateRemark: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        data: Partial<Omit<APIRemark, 'validation_key' | 'item_key'>>;
      }>,
    ) => {
      state.remarks = state.remarks.map((remark: any) => {
        if (
          remark.validation_key === action.payload.validation_key &&
          remark.item_key === action.payload.item_key
        ) {
          return {
            ...remark,
            ...action.payload.data,
          };
        }
        return remark;
      });
    },

    addOrUpdateRemark: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        data: Partial<Omit<APIRemark, 'validation_key' | 'item_key'>>;
      }>,
    ) => {
      const existingRemarkIndex = state.remarks.findIndex(
        (remark) =>
          remark.validation_key === action.payload.validation_key &&
          remark.item_key === action.payload.item_key,
      );

      if (existingRemarkIndex !== -1) {
        // Update existing remark
        state.remarks[existingRemarkIndex] = {
          ...state.remarks[existingRemarkIndex],
          ...action.payload.data,
        };
      } else {
        // Add new remark with required fields and provided data
        const newRemark: APIRemark = {
          validation_key: action.payload.validation_key,
          item_key: action.payload.item_key,
          status: 'approved',
          text: '',
          text_validator_log_id: null,
          ai_result: '',
          section: null,
          ...action.payload.data, // Override defaults with provided data
        };
        state.remarks.push(newRemark);
      }
    },

    addFile: (state, action: PayloadAction<APIRemarkFile>) => {
      const existing = state.files.find(
        (file) =>
          file.validation_key === action.payload.validation_key &&
          file.item_key === action.payload.item_key,
      );
      if (!existing) {
        state.files.push(action.payload);
        return;
      }
      existing.text = action.payload.text;
      if (action.payload.mandatory && !existing.mandatory) {
        existing.mandatory = true;
        if (existing.is_deferred) {
          existing.is_deferred = false;
          existing.status = 'created';
        }
      }
    },

    removeFile: (
      state,
      action: PayloadAction<{ validation_key: string; item_key: string }>,
    ) => {
      state.files = state.files.filter(
        (file) =>
          !(
            file.validation_key === action.payload.validation_key &&
            file.item_key === action.payload.item_key
          ),
      );
    },

    updateFile: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        new_file?: {
          name: string;
          file: string;
          id: number;
          created_at: string;
        };
        file_validator_log_id?: number | null;
        status?: APIRemarkStatus;
        ai_result?: string;
      }>,
    ) => {
      state.files = state.files.map((file) => {
        if (
          file.validation_key === action.payload.validation_key &&
          file.item_key === action.payload.item_key
        ) {
          const updatesFile = {
            ...file,
            ...(action.payload.status && { status: action.payload.status }),
            ...(action.payload.file_validator_log_id !== undefined && {
              file_validator_log_id: action.payload.file_validator_log_id,
            }),
            ...(action.payload.ai_result && {
              ai_result: action.payload.ai_result,
            }),
          };

          if (action.payload.new_file) {
            updatesFile.files = [
              ...file.files,
              {
                id: action.payload.new_file.id,
                name: action.payload.new_file.name,
                url: action.payload.new_file.file,
                created_at: action.payload.new_file.created_at,
              },
            ];
          }

          return updatesFile;
        }
        return file;
      });
    },

    setFileDeferredStatus: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        is_deferred: boolean;
      }>,
    ) => {
      state.files = state.files.map((file) => {
        if (
          file.validation_key === action.payload.validation_key &&
          file.item_key === action.payload.item_key
        ) {
          return {
            ...file,
            is_deferred: action.payload.is_deferred,
            status: action.payload.is_deferred ? 'pending' : 'created',
          };
        }
        return file;
      });
    },

    removeSubFile: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        id: number;
      }>,
    ) => {
      state.files = state.files.map((file) => {
        if (
          file.validation_key === action.payload.validation_key &&
          file.item_key === action.payload.item_key
        ) {
          const remainingFiles = file.files.filter(
            (f) => f.id !== action.payload.id,
          );
          return {
            ...file,
            files: remainingFiles,
            status: file.is_deferred ? 'pending' : 'created',
            file_validator_log_id: null,
            ai_result: '',
          };
        }
        return file;
      });
    },

    changePolicyInformation: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      if (state.data && state.data.policy_information) {
        (state.data.policy_information as any)[action.payload.name] =
          action.payload.value;
      }
    },

    changeApplicantInformation: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      if (state.data && state.data.applicant_information) {
        (state.data.applicant_information as any)[action.payload.name] =
          action.payload.value;

        // Auto-sync first account holder if it's set to same as applicant
        if (state.data.account_holders.first.is_same_as_applicant) {
          const syncFields = [
            'address',
            'city',
            'province_code',
            'postal_code',
            'phone_home',
          ];
          if (syncFields.includes(action.payload.name)) {
            if (action.payload.name === 'phone_home') {
              state.data.account_holders.first.phone = action.payload.value;
            } else {
              state.data.account_holders.first = {
                ...state.data.account_holders.first,
                [action.payload.name]: action.payload.value,
              };
            }
          }
        }
      }
    },

    changeBrokerInformation: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      if (state.data && state.data.broker_information) {
        (state.data.broker_information as any)[action.payload.name] =
          action.payload.value;
      }
    },

    changePolicyPeriod: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      if (state.data && state.data.policy_period) {
        (state.data.policy_period as any)[action.payload.name] =
          action.payload.value;
      }
    },

    changeApplicants: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.applicants &&
        state.data.applicants[action.payload.index]
      ) {
        (state.data.applicants[action.payload.index] as any)[
          action.payload.name
        ] = action.payload.value;

        // Auto-sync first account holder if it's set to same as applicant and we're updating the first applicant
        if (
          action.payload.index === 0 &&
          state.data.account_holders.first.is_same_as_applicant
        ) {
          const syncFields = ['email', 'name'];
          if (syncFields.includes(action.payload.name)) {
            if (action.payload.name === 'name') {
              // Split the name by spaces - first word as first name, rest as last name
              const nameParts = (action.payload.value || '').trim().split(' ');
              const firstName = nameParts[0] || '';
              const lastName = nameParts.slice(1).join(' ') || '';

              state.data.account_holders.first = {
                ...state.data.account_holders.first,
                first_name: firstName,
                last_name: lastName,
              };
            } else {
              state.data.account_holders.first = {
                ...state.data.account_holders.first,
                [action.payload.name]: action.payload.value,
              };
            }
          }
        }

        // Auto-sync second account holder if it's set to same as co-applicant and we're updating the second applicant
        if (
          action.payload.index === 1 &&
          state.data.account_holders.second.is_same_as_co_applicant
        ) {
          const syncFields = ['email', 'name'];
          if (syncFields.includes(action.payload.name)) {
            if (action.payload.name === 'name') {
              // Split the name by spaces - first word as first name, rest as last name
              const nameParts = (action.payload.value || '').trim().split(' ');
              const firstName = nameParts[0] || '';
              const lastName = nameParts.slice(1).join(' ') || '';

              state.data.account_holders.second = {
                ...state.data.account_holders.second,
                first_name: firstName,
                last_name: lastName,
              };
            } else {
              state.data.account_holders.second = {
                ...state.data.account_holders.second,
                [action.payload.name]: action.payload.value,
              };
            }
          }
        }
      }
    },

    changeBrokerQuestionnaire: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      if (state.data && state.data.broker_questionnaire) {
        (state.data.broker_questionnaire as any)[action.payload.name] =
          action.payload.value;
      }
    },

    changeDisclosureAndAttestation: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      state.data.disclosure_and_attestation = {
        ...state.data.disclosure_and_attestation,
        [action.payload.name]: action.payload.value,
      };
    },

    changeBilling: (
      state,
      action: PayloadAction<{ name: string; value: string | boolean }>,
    ) => {
      state.data.billing = {
        ...state.data.billing,
        [action.payload.name]: action.payload.value,
      };

      if (action.payload.name === 'payment_schedule') {
        state.data.billing.payment_date = null;
        state.data.billing.payment_day_of_month = null;
        state.data.billing.payment_method = null;
        state.data.billing.has_down_payment = null;
        state.data.billing.down_payment_method = null;
        state.data.billing.down_payment_amount = null;
        state.data.billing.down_payment_date = null;
      }

      if (!state.data.billing.has_down_payment) {
        state.data.billing.down_payment_method = null;
        state.data.billing.down_payment_amount = null;
        state.data.billing.down_payment_date = null;
      }
    },

    changeMethodOfPayment: (
      state,
      action: PayloadAction<{ name: string; value: string | number | boolean }>,
    ) => {
      state.data.method_of_payment = {
        ...state.data.method_of_payment,
        [action.payload.name]: action.payload.value,
      };
    },

    updatePayments: (state, action: PayloadAction<APIApplicationPayment[]>) => {
      state.payments = action.payload;
    },

    changeAccountHolder: (
      state,
      action: PayloadAction<{
        holder: 'first' | 'second';
        name: string;
        value: string | boolean;
      }>,
    ) => {
      if (action.payload.holder === 'first') {
        state.data.account_holders.first = {
          ...state.data.account_holders.first,
          [action.payload.name]: action.payload.value,
        };

        // If setting is_same_as_applicant to true, sync with first applicant data
        if (
          action.payload.name === 'is_same_as_applicant' &&
          action.payload.value === true &&
          state.data.applicants &&
          state.data.applicants[0]
        ) {
          const firstApplicant = state.data.applicants[0];
          // Split the name by spaces - first word as first name, rest as last name
          const nameParts = (firstApplicant.name || '').trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          state.data.account_holders.first = {
            ...state.data.account_holders.first,
            email: firstApplicant.email,
            first_name: firstName,
            last_name: lastName,
            is_same_as_co_applicant: false,
          };
        }

        if (
          action.payload.name === 'is_same_as_co_applicant' &&
          action.payload.value === true &&
          state.data.applicants &&
          state.data.applicants[1]
        ) {
          const secondApplicant = state.data.applicants[1];
          const nameParts = (secondApplicant.name || '').trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          state.data.account_holders.first = {
            ...state.data.account_holders.first,
            email: secondApplicant.email,
            first_name: firstName,
            last_name: lastName,
            // Use applicant_information for address/phone
            address:
              state.data.applicant_information?.address ||
              state.data.account_holders.first.address,
            city:
              state.data.applicant_information?.city ||
              state.data.account_holders.first.city,
            province_code:
              state.data.applicant_information?.province_code ||
              state.data.account_holders.first.province_code,
            postal_code:
              state.data.applicant_information?.postal_code ||
              state.data.account_holders.first.postal_code,
            phone:
              state.data.applicant_information?.phone_home ||
              state.data.account_holders.first.phone,
            is_same_as_applicant: false,
          };
        }
      } else {
        state.data.account_holders.second = {
          ...state.data.account_holders.second,
          [action.payload.name]: action.payload.value,
        };

        // If setting is_same_as_co_applicant to true, sync with second applicant data
        if (
          action.payload.name === 'is_same_as_co_applicant' &&
          action.payload.value === true &&
          state.data.applicants &&
          state.data.applicants[1]
        ) {
          const secondApplicant = state.data.applicants[1];
          // Split the name by spaces - first word as first name, rest as last name
          const nameParts = (secondApplicant.name || '').trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          state.data.account_holders.second = {
            ...state.data.account_holders.second,
            email: secondApplicant.email,
            first_name: firstName,
            last_name: lastName,
          };
        }
      }
    },

    changeAgencyBilling: (
      state,
      action: PayloadAction<{ name: string; value: string | boolean }>,
    ) => {
      if (!state.data) return;

      const data = state.data;
      if (action.payload.name === 'financing_type') {
        data.agency_billing.financing_type = action.payload.value as
          | 'paid_full'
          | 'premium_financing';
      }

      if (action.payload.name === 'is_signed_contract_attached') {
        data.agency_billing.is_signed_contract_attached = action.payload
          .value as boolean;
      }

      if (action.payload.name === 'province_code') {
        data.agency_billing.province_code = action.payload.value as string;
      }

      if (action.payload.name === 'business_code') {
        data.agency_billing.business_code = action.payload.value as string;
      }

      if (action.payload.name === 'total_premium') {
        data.agency_billing.total_premium = action.payload.value as string;
      }
      if (action.payload.name === 'service_fee') {
        data.agency_billing.service_fee = action.payload.value as string;
      }
    },

    addAdditionalInsured: (state) => {
      if (!state.data) return;
      if (!state.data.additional_insureds) {
        state.data.additional_insureds = [];
      }
      if (state.data.additional_insureds.length >= 2) return;
      state.data.additional_insureds.push({
        name: '',
        email: '',
        date_of_birth: '',
        occupation: '',
        interest: '',
      });
    },

    changeAdditionalInsured: (
      state,
      action: PayloadAction<{ index: number; name: string; value: string }>,
    ) => {
      if (!state.data?.additional_insureds?.[action.payload.index]) return;
      (state.data.additional_insureds[action.payload.index] as any)[
        action.payload.name
      ] = action.payload.value;
    },

    removeAdditionalInsured: (state, action: PayloadAction<number>) => {
      if (!state.data?.additional_insureds) return;
      state.data.additional_insureds.splice(action.payload, 1);
    },

    changeLossHistory: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      if (state.data && state.data.loss_history) {
        (state.data.loss_history as any)[action.payload.name] =
          action.payload.value;
      }
    },

    changeLosses: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.loss_history &&
        state.data.loss_history.losses &&
        state.data.loss_history.losses[action.payload.index]
      ) {
        (state.data.loss_history.losses[action.payload.index] as any)[
          action.payload.name
        ] = action.payload.value;
      }
    },

    addLoss: (state) => {
      if (
        state.data &&
        state.data.loss_history &&
        state.data.loss_history.losses
      ) {
        const newLoss = {
          amount_paid: '',
          cause_of_loss: '',
          date: '',
          insurance_company: '',
          loc_no: '',
          policy_number: '',
          status: '' as '',
        };
        state.data.loss_history.losses.push(newLoss);
      }
    },

    removeLoss: (state, action: PayloadAction<number>) => {
      if (
        state.data &&
        state.data.loss_history &&
        state.data.loss_history.losses &&
        state.data.loss_history.losses.length > action.payload
      ) {
        state.data.loss_history.losses.splice(action.payload, 1);
        state.data.loss_history.losses.forEach((loss, index) => {
          loss.loc_no = (index + 1).toString();
        });
      }
    },

    changePolicyHistory: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      if (state.data && state.data.policy_history) {
        (state.data.policy_history as any)[action.payload.name] =
          action.payload.value;
      }
    },

    changePolicies: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.policy_history &&
        state.data.policy_history.policies &&
        state.data.policy_history.policies[action.payload.index]
      ) {
        (state.data.policy_history.policies[action.payload.index] as any)[
          action.payload.name
        ] = action.payload.value;
      }
    },

    addPolicy: (state) => {
      if (
        state.data &&
        state.data.policy_history &&
        state.data.policy_history.policies
      ) {
        const newPolicy = {
          effective_date: '',
          expiration_date: '',
          insurance_company: '',
          policy_number: '',
          reason_for_ending: '',
          terminated_by_insurer_reason: '',
          is_current: false,
        };
        state.data.policy_history.policies.push(newPolicy);
      }
    },

    removePolicy: (state, action: PayloadAction<number>) => {
      if (
        state.data &&
        state.data.policy_history &&
        state.data.policy_history.policies &&
        state.data.policy_history.policies.length > action.payload
      ) {
        state.data.policy_history.policies.splice(action.payload, 1);
      }
    },

    changeCrossReferences: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.cross_reference_information &&
        state.data.cross_reference_information[action.payload.index]
      ) {
        (state.data.cross_reference_information[action.payload.index] as any)[
          action.payload.name
        ] = action.payload.value;
      }
    },

    changeDwellingUnderwritingHeader: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.index] &&
        state.data.dwellings[action.payload.index].underwriting_header
      ) {
        (state.data.dwellings[action.payload.index].underwriting_header as any)[
          action.payload.name
        ] = action.payload.value;
      }
    },

    changeDwellingRiskAddress: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.index] &&
        state.data.dwellings[action.payload.index].risk_address
      ) {
        (state.data.dwellings[action.payload.index].risk_address as any)[
          action.payload.name
        ] = action.payload.value;
      }
    },

    changeDwellingRatingInformation: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.index] &&
        state.data.dwellings[action.payload.index].rating_information
      ) {
        (state.data.dwellings[action.payload.index].rating_information as any)[
          action.payload.name
        ] = action.payload.value;
      }
    },

    changeDwellingLiabilityExposures: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.index] &&
        state.data.dwellings[action.payload.index].liability_exposures
      ) {
        (state.data.dwellings[action.payload.index].liability_exposures as any)[
          action.payload.name
        ] = action.payload.value;
      }
    },

    changeDwellingCoverages: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.index] &&
        state.data.dwellings[action.payload.index].coverages
      ) {
        (state.data.dwellings[action.payload.index].coverages as any)[
          action.payload.name
        ] = action.payload.value;
      }
    },

    changeDwellingCoveragesItem: (
      state,
      action: PayloadAction<{
        dwellingIndex: number;
        itemIndex: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].coverages &&
        state.data.dwellings[action.payload.dwellingIndex].coverages.items &&
        state.data.dwellings[action.payload.dwellingIndex].coverages.items[
          action.payload.itemIndex
        ]
      ) {
        (
          state.data.dwellings[action.payload.dwellingIndex].coverages.items[
            action.payload.itemIndex
          ] as any
        )[action.payload.name] = action.payload.value;
      }
    },

    addDwellingCoveragesItem: (state, action: PayloadAction<number>) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload] &&
        state.data.dwellings[action.payload].coverages &&
        state.data.dwellings[action.payload].coverages.items
      ) {
        state.data.dwellings[action.payload].coverages.items.push({
          coverage_description: '',
          requested_declined: '',
          amount_of_insurance: '',
          deductible: '',
          deductible_type: '',
          type_of_1: '',
          type_of_2: '',
          type_of_3: '',
          type_of_4: '',
          type_of_5: '',
          estimated_premium: '',
        });
      }
    },

    removeDwellingCoveragesItem: (
      state,
      action: PayloadAction<{ dwellingIndex: number; itemIndex: number }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].coverages &&
        state.data.dwellings[action.payload.dwellingIndex].coverages.items
      ) {
        state.data.dwellings[
          action.payload.dwellingIndex
        ].coverages.items.splice(action.payload.itemIndex, 1);
      }
    },

    changeDwellingLiabilityExtensionsExclusionsItem: (
      state,
      action: PayloadAction<{
        dwellingIndex: number;
        itemIndex: number;
        name: string;
        value: string;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex]
          .liability_extensions_exclusions &&
        state.data.dwellings[action.payload.dwellingIndex]
          .liability_extensions_exclusions[action.payload.itemIndex]
      ) {
        (
          state.data.dwellings[action.payload.dwellingIndex]
            .liability_extensions_exclusions[action.payload.itemIndex] as any
        )[action.payload.name] = action.payload.value;
      }
    },

    addDwellingLiabilityExtensionsExclusionsItem: (
      state,
      action: PayloadAction<number>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload] &&
        state.data.dwellings[action.payload].liability_extensions_exclusions
      ) {
        state.data.dwellings[
          action.payload
        ].liability_extensions_exclusions.push({
          liability_coverage_description: '',
          requested_declined: '',
          amount_of_insurance: '',
          deductible: '',
          deductible_type: '',
          type_1: '',
          type_2: '',
          type_3: '',
          type_4: '',
          type_5: '',
          estimated_premium: '',
        });
      }
    },

    removeDwellingLiabilityExtensionsExclusionsItem: (
      state,
      action: PayloadAction<{ dwellingIndex: number; itemIndex: number }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex]
          .liability_extensions_exclusions
      ) {
        state.data.dwellings[
          action.payload.dwellingIndex
        ].liability_extensions_exclusions.splice(action.payload.itemIndex, 1);
      }
    },

    changeDwellingDiscountsSurchargesItem: (
      state,
      action: PayloadAction<{
        dwellingIndex: number;
        itemIndex: number;
        name: string;
        value: string | boolean | null;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex]
          .discounts_surcharges &&
        state.data.dwellings[action.payload.dwellingIndex].discounts_surcharges[
          action.payload.itemIndex
        ]
      ) {
        (
          state.data.dwellings[action.payload.dwellingIndex]
            .discounts_surcharges[action.payload.itemIndex] as any
        )[action.payload.name] = action.payload.value;
      }
    },

    addDwellingDiscountsSurchargesItem: (
      state,
      action: PayloadAction<number>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload] &&
        state.data.dwellings[action.payload].discounts_surcharges
      ) {
        state.data.dwellings[action.payload].discounts_surcharges.push({
          description: '',
          percentage: '',
          has_applied_to_premium: null,
          estimated_discount_surcharge: '',
        });
      }
    },

    removeDwellingDiscountsSurchargesItem: (
      state,
      action: PayloadAction<{ dwellingIndex: number; itemIndex: number }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].discounts_surcharges
      ) {
        state.data.dwellings[
          action.payload.dwellingIndex
        ].discounts_surcharges.splice(action.payload.itemIndex, 1);
      }
    },

    changeDwellingMortgagee: (
      state,
      action: PayloadAction<{
        dwellingIndex: number;
        mortgageeIndex: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].mortgagee &&
        state.data.dwellings[action.payload.dwellingIndex].mortgagee[
          action.payload.mortgageeIndex
        ]
      ) {
        (
          state.data.dwellings[action.payload.dwellingIndex].mortgagee[
            action.payload.mortgageeIndex
          ] as any
        )[action.payload.name] = action.payload.value;
      }
    },

    addDwellingMortgagee: (state, action: PayloadAction<number>) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload] &&
        state.data.dwellings[action.payload].mortgagee
      ) {
        const newMortgagee = {
          name: '',
          address: '',
          nature_of_interest: '',
          city: '',
          province_code: '',
          postal_code: '',
        };
        state.data.dwellings[action.payload].mortgagee.push(newMortgagee);
      }
    },

    removeDwellingMortgagee: (
      state,
      action: PayloadAction<{ dwellingIndex: number; mortgageeIndex: number }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].mortgagee &&
        state.data.dwellings[action.payload.dwellingIndex].mortgagee.length >
          action.payload.mortgageeIndex
      ) {
        state.data.dwellings[action.payload.dwellingIndex].mortgagee.splice(
          action.payload.mortgageeIndex,
          1,
        );
      }
    },

    changeDwellingAttachment: (
      state,
      action: PayloadAction<{
        dwellingIndex: number;
        attachmentIndex: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].attachments &&
        state.data.dwellings[action.payload.dwellingIndex].attachments[
          action.payload.attachmentIndex
        ]
      ) {
        (
          state.data.dwellings[action.payload.dwellingIndex].attachments[
            action.payload.attachmentIndex
          ] as any
        )[action.payload.name] = action.payload.value;
      }
    },

    addDwellingAttachment: (state, action: PayloadAction<number>) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload] &&
        state.data.dwellings[action.payload].attachments
      ) {
        const newAttachment = {
          description: '',
          date_completed: '',
        };
        state.data.dwellings[action.payload].attachments.push(newAttachment);
      }
    },

    removeDwellingAttachment: (
      state,
      action: PayloadAction<{ dwellingIndex: number; attachmentIndex: number }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].attachments &&
        state.data.dwellings[action.payload.dwellingIndex].attachments.length >
          action.payload.attachmentIndex
      ) {
        state.data.dwellings[action.payload.dwellingIndex].attachments.splice(
          action.payload.attachmentIndex,
          1,
        );
      }
    },

    changeDwellingAddressHistory: (
      state,
      action: PayloadAction<{
        dwellingIndex: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].address_history
      ) {
        (
          state.data.dwellings[action.payload.dwellingIndex]
            .address_history as any
        )[action.payload.name] = action.payload.value;
      }
    },

    changeDwellingAddressHistoryItem: (
      state,
      action: PayloadAction<{
        dwellingIndex: number;
        itemIndex: number;
        name: string;
        value: any;
      }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].address_history &&
        state.data.dwellings[action.payload.dwellingIndex].address_history
          .items &&
        state.data.dwellings[action.payload.dwellingIndex].address_history
          .items[action.payload.itemIndex]
      ) {
        (
          state.data.dwellings[action.payload.dwellingIndex].address_history
            .items[action.payload.itemIndex] as any
        )[action.payload.name] = action.payload.value;
      }
    },

    addDwellingAddressHistoryItem: (state, action: PayloadAction<number>) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload] &&
        state.data.dwellings[action.payload].address_history &&
        state.data.dwellings[action.payload].address_history.items
      ) {
        const newItem = {
          address: '',
          city: '',
          province: '',
          postal_code: '',
          date_moved_in: '',
          date_moved_out: '',
        };
        state.data.dwellings[action.payload].address_history.items.push(
          newItem,
        );
      }
    },

    removeDwellingAddressHistoryItem: (
      state,
      action: PayloadAction<{ dwellingIndex: number; itemIndex: number }>,
    ) => {
      if (
        state.data &&
        state.data.dwellings &&
        state.data.dwellings[action.payload.dwellingIndex] &&
        state.data.dwellings[action.payload.dwellingIndex].address_history &&
        state.data.dwellings[action.payload.dwellingIndex].address_history
          .items &&
        state.data.dwellings[action.payload.dwellingIndex].address_history.items
          .length > action.payload.itemIndex
      ) {
        state.data.dwellings[
          action.payload.dwellingIndex
        ].address_history.items.splice(action.payload.itemIndex, 1);
      }
    },

    changeIsDataSaved: (state, action: PayloadAction<boolean>) => {
      if (state.signing !== null) {
        state.isDataSaved = true;
        return;
      }

      state.isDataSaved = action.payload;
    },

    setBinderPDF: (state, action: PayloadAction<APIBinderPDF>) => {
      state.binder_pdf = action.payload;
    },

    setSigning: (state, action: PayloadAction<APISigning | null>) => {
      state.signing = action.payload;
    },

    addSupplement: (state, action: PayloadAction<APIsupplementFile>) => {
      state.supplements.push(action.payload);
    },

    removeSupplement: (state, action: PayloadAction<number>) => {
      state.supplements = state.supplements.filter(
        (supplement) => supplement.id !== action.payload,
      );
    },

    updateWipStatus: (state, action: PayloadAction<number>) => {
      state.wip_status = action.payload;
    },

    setNotesToUnderwriter: (state, action: PayloadAction<string>) => {
      if (state.data) {
        state.data.notes_to_underwriter = action.payload;
      }
    },
  },
});

export const {
  storeData,
  resetData,
  addMissingMandatoryField,
  removeMissingMandatoryField,
  addRemark,
  removeRemark,
  updateRemark,
  addOrUpdateRemark,
  addFile,
  removeFile,
  updateFile,
  changePolicyInformation,
  changeApplicantInformation,
  changeBrokerInformation,
  changePolicyPeriod,
  changeApplicants,
  changeBrokerQuestionnaire,
  changeDisclosureAndAttestation,
  changeBilling,
  changeMethodOfPayment,
  updatePayments,
  changeAccountHolder,
  changeAgencyBilling,
  addAdditionalInsured,
  changeAdditionalInsured,
  removeAdditionalInsured,
  changeLossHistory,
  changeLosses,
  addLoss,
  removeLoss,
  changePolicyHistory,
  changePolicies,
  addPolicy,
  removePolicy,
  changeCrossReferences,
  changeDwellingUnderwritingHeader,
  changeDwellingRiskAddress,
  changeDwellingRatingInformation,
  changeDwellingLiabilityExposures,
  changeDwellingCoverages,
  changeDwellingCoveragesItem,
  addDwellingCoveragesItem,
  removeDwellingCoveragesItem,
  changeDwellingLiabilityExtensionsExclusionsItem,
  addDwellingLiabilityExtensionsExclusionsItem,
  removeDwellingLiabilityExtensionsExclusionsItem,
  changeDwellingDiscountsSurchargesItem,
  addDwellingDiscountsSurchargesItem,
  removeDwellingDiscountsSurchargesItem,
  changeDwellingMortgagee,
  addDwellingMortgagee,
  removeDwellingMortgagee,
  changeDwellingAttachment,
  addDwellingAttachment,
  removeDwellingAttachment,
  changeDwellingAddressHistory,
  changeDwellingAddressHistoryItem,
  addDwellingAddressHistoryItem,
  removeDwellingAddressHistoryItem,
  changeIsDataSaved,
  setBinderPDF,
  setSigning,
  setFileDeferredStatus,
  removeSubFile,
  addSupplement,
  removeSupplement,
  updateWipStatus,
  setNotesToUnderwriter,
} = homePersonalFormSlice.actions;

export default homePersonalFormSlice.reducer;
